// ===========================================================================
// The Lumi Plus page sidebar: sign in / create an account (18+), then upgrade to
// Plus (monthly or yearly). Reuses the account client and the shared auth form.
// Handles the return from checkout (?billing=stub|success). ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el, toast } from '../dom.js';
import { AccountClient } from '../account.js';
import { renderAuth } from './auth.js';
import { PLUS_PRICES } from '@lumi/shared';

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

export function mountPlusSignup(container) {
  const account = new AccountClient();
  // Whether checkout can actually complete. This page exists to sell Plus, so
  // it must not put a price and a button in front of someone when the answer
  // to pressing it is "unavailable". null until asked; nothing is sold while
  // it is null.
  let checkoutOpen = null;

  function buy(sku) {
    account.buy({ kind: 'plus', sku }).then((r) => {
      if (r.redirecting) return;
      if (r.ok) {
        toast('Welcome to Lumi Plus!');
        render();
      } else {
        toast(r.error === 'price_not_configured' ? 'Not configured yet.' : 'Checkout is unavailable right now.');
      }
    });
  }

  function render() {
    container.replaceChildren();
    if (!account.loggedIn) {
      container.append(
        el('h2', { class: 'side-title', text: t('Get Lumi Plus') }),
        el('p', {
          class: 'side-sub',
          text: checkoutOpen === false
            ? 'Create a free account (18+) or sign in. Plus is not on sale yet. Lumi stays free either way.'
            : 'Create a free account (18+) or sign in, then choose a plan. Lumi stays free either way.',
        }),
        renderAuth(account, () => render()),
      );
      return;
    }
    if (account.isPlus) {
      container.append(
        el('div', { class: 'plus-badge-lg' }, [el('span', { class: 'plus-badge', text: 'PLUS' })]),
        el('h2', { class: 'side-title', text: `Hi, ${account.account.username}` }),
        el('p', { class: 'plus-active', text: t('You are a Plus member. Thank you for keeping Lumi independent and ad-free.') }),
        el('a', { class: 'btn btn-secondary', href: '/', text: t('Back to Lumi') }),
        el('button', { class: 'btn btn-ghost btn-sm', text: t('Sign out'), onClick: () => { account.logout(); render(); } }),
      );
      return;
    }
    const yearlyMonthly = PLUS_PRICES.yearlyCents / 12;
    const savePct = Math.round((1 - yearlyMonthly / PLUS_PRICES.monthlyCents) * 100);
    container.append(
      el('h2', { class: 'side-title', text: `Signed in as ${account.account.username}` }),
      el('p', {
        class: 'side-sub',
        text: checkoutOpen
          ? 'Choose a plan to unlock Plus. Cancel anytime.'
          : 'These are the planned plans. Nothing to choose yet.',
      }),
      el('div', { class: 'plan-card' }, [
        el('div', { class: 'plan-name', text: t('Monthly') }),
        el('div', { class: 'plan-price' }, [el('strong', { text: dollars(PLUS_PRICES.monthlyCents) }), el('span', { text: '/mo' })]),
        checkoutOpen
          ? el('button', { class: 'btn btn-primary', text: t('Go Plus monthly'), onClick: () => buy('monthly') })
          : el('div', { class: 'plan-closed', text: t('Not open yet') }),
      ]),
      el('div', { class: 'plan-card featured' }, [
        el('div', { class: 'plan-name' }, [el('span', { text: t('Yearly') }), savePct > 0 ? el('span', { class: 'plan-save', text: `Save ${savePct}%` }) : null].filter(Boolean)),
        el('div', { class: 'plan-price' }, [el('strong', { text: dollars(PLUS_PRICES.yearlyCents) }), el('span', { text: '/yr' })]),
        checkoutOpen
          ? el('button', { class: 'btn btn-primary', text: t('Go Plus yearly'), onClick: () => buy('yearly') })
          : el('div', { class: 'plan-closed', text: t('Not open yet') }),
      ]),
      checkoutOpen
        ? null
        : el('p', { class: 'side-sub', text: t('Plus is not on sale yet: these are the planned prices. Lumi is free and complete without it, and your account is ready for the day it opens.') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Sign out'), onClick: () => { account.logout(); render(); } }),
    );
  }

  (async () => {
    await account.refresh().catch(() => {});
    checkoutOpen = await fetch('/api/billing/enabled')
      .then((r) => r.json())
      .then(({ enabled }) => !!enabled)
      .catch(() => false); // unreachable -> assume closed rather than offer a dead button
    const params = new URLSearchParams(location.search);
    const flow = params.get('billing');
    if (flow === 'stub' && params.get('token')) {
      await account.stubComplete(params.get('token'));
      toast('Purchase complete.');
    } else if (flow === 'success') {
      await account.refresh().catch(() => {});
      toast('Thanks! Your Plus is active.');
    }
    if (flow) history.replaceState({}, '', location.pathname);
    render();
  })();
}

export default { mountPlusSignup };
